import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Badge,
  useTheme,
  useMediaQuery,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Tooltip,
  Alert,
  Paper,
  Fade,
  Collapse
} from '@mui/material';
import {
  Dashboard,
  FolderOpen,
  ViewKanban,
  Schedule,
  Group,
  BugReport,
  EventNote,
  AccessTime,
  Event,
  Assessment,
  Speed,
  People,
  AdminPanelSettings,
  CheckCircle,
  Approval,
  Analytics,
  Settings,
  Notifications,
  Menu as MenuIcon,
  Close,
  ChevronRight,
  Business,
  CalendarMonth,
  Assignment,
  Timeline,
  Add,
  ExpandMore,
  ExpandLess,
  MoreVert
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { UserRole } from '../../types';
import CreateProjectModal from '../Common/CreateProjectModal';

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const isTablet = useMediaQuery(theme.breakpoints.down('xl'));
  
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuAnchor, setProfileMenuAnchor] = useState<null | HTMLElement>(null);
  const [createProjectModalOpen, setCreateProjectModalOpen] = useState(false);
  const [projectCreateStatus, setProjectCreateStatus] = useState<string>('');
  const [moreMenuAnchor, setMoreMenuAnchor] = useState<null | HTMLElement>(null);
  const [dropdownAnchors, setDropdownAnchors] = useState<{ [key: string]: HTMLElement | null }>({});

  const handleNavigation = (path: string) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setProfileMenuAnchor(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setProfileMenuAnchor(null);
  };

  const handleLogout = () => {
    logout();
    handleProfileMenuClose();
    navigate('/login');
  };

  const handleCreateProject = () => {
    setCreateProjectModalOpen(true);
  };

  const handleDropdownOpen = (category: string, event: React.MouseEvent<HTMLElement>) => {
    setDropdownAnchors(prev => ({ ...prev, [category]: event.currentTarget }));
  };

  const handleDropdownClose = (category: string) => {
    setDropdownAnchors(prev => ({ ...prev, [category]: null }));
  };

  const handleDropdownItemClick = (item: any) => {
    if (item.action === 'createProject') {
      handleCreateProject();
    } else if (item.path) {
      handleNavigation(item.path);
    }
    // Close all dropdowns
    setDropdownAnchors({});
  };

  const handleCreateProjectSuccess = (project: any) => {
    setProjectCreateStatus('Project created successfully!');
    setTimeout(() => setProjectCreateStatus(''), 3000);
    
    // If user is on Projects page, refresh it to show the synced Tyvra PM project
    if (location.pathname === '/projects') {
      // Trigger a page refresh to reload projects
      window.location.reload();
    }
  };

  const handleCreateProjectError = (error: string) => {
    setProjectCreateStatus(`Error: ${error}`);
    setTimeout(() => setProjectCreateStatus(''), 5000);
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const isVisible = (roles: UserRole[]) => {
    return user && roles.includes(user.role);
  };

  // Main navigation items (always visible)
  const mainNavItems = [
    { label: 'Dashboard', path: '/dashboard', icon: Dashboard, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
    { label: 'Issues', path: '/issues', icon: BugReport, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
  ];

  // Categorized navigation with dropdowns
  const categorizedNav = [
    {
      label: 'Projects',
      icon: FolderOpen,
      roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'All Projects', path: '/projects', icon: FolderOpen },
        { label: 'Create Project', action: 'createProject', icon: Add },
        { label: 'Project Assignments', path: '/project-assignments', icon: Assignment },
      ]
    },
    {
      label: 'Sprints',
      icon: Schedule,
      roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'All Sprints', path: '/sprints', icon: Schedule },
        { label: 'Boards', path: '/boards', icon: ViewKanban },
        { label: 'Planning', path: '/planning', icon: Timeline },
      ]
    },
    {
      label: 'Time & HR',
      icon: AccessTime,
      roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'Timesheets', path: '/timesheets', icon: AccessTime },
        { label: 'Leaves', path: '/leaves', icon: Event },
        { label: 'Leave Calendar', path: '/leaves/calendar', icon: CalendarMonth },
      ]
    },
    {
      label: 'Approvals',
      icon: CheckCircle,
      roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'Timesheet Approvals', path: '/timesheets/approvals', icon: Approval },
        { label: 'Leave Approvals', path: '/leaves/approvals', icon: CheckCircle },
      ]
    },
    {
      label: 'Management',
      icon: AdminPanelSettings,
      roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'Teams', path: '/teams', icon: Group },
        { label: 'Users', path: '/users', icon: People },
        { label: 'Employees', path: '/employees', icon: Business },
      ]
    },
    {
      label: 'Analytics',
      icon: Analytics,
      roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'Productivity Analytics', path: '/productivity-analytics', icon: Analytics },
        { label: 'User Productivity', path: '/user-productivity', icon: People },
        { label: 'Historical Analytics', path: '/estimation/analytics', icon: Analytics },
        { label: 'Leadership Dashboard', path: '/leadership-dashboard', icon: Assessment },
        { label: 'Custom KPI Builder', path: '/custom-kpis', icon: Assessment },
      ]
    },
    {
      label: 'Tools',
      icon: Assessment,
      roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER],
      items: [
        { label: 'Estimation Tool', path: '/estimation', icon: Assessment },
        { label: 'Advanced Estimation', path: '/estimation/advanced', icon: Assessment },
        { label: 'Estimation Templates', path: '/estimation/templates', icon: Assignment },
        { label: 'Estimation Config', path: '/estimation/config', icon: Settings },
      ]
    }
  ];

  // Navigation items organized by category for mobile
  const navigationItems = [
    {
      category: 'Core',
      items: [
        { label: 'Dashboard', path: '/dashboard', icon: Dashboard, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Projects', path: '/projects', icon: FolderOpen, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Boards', path: '/boards', icon: ViewKanban, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Sprints', path: '/sprints', icon: Schedule, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Retrospectives', path: '/retrospectives', icon: Assessment, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Velocity', path: '/velocity', icon: Speed, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN] },
        { label: 'Timesheet Calendar', path: '/timesheets/calendar', icon: AccessTime, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN] },
      ]
    },
    {
      category: 'Work Management',
      items: [
        { label: 'Issues', path: '/issues', icon: BugReport, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Planning', path: '/planning', icon: Timeline, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Estimation', path: '/estimation', icon: Assessment, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ROOT_USER] },
      ]
    },
    {
      category: 'Time & Attendance',
      items: [
        { label: 'Timesheets', path: '/timesheets', icon: AccessTime, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Leaves', path: '/leaves', icon: Event, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
      ]
    },
    {
      category: 'Administration',
      items: [
        { label: 'Teams', path: '/teams', icon: Group, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Users', path: '/users', icon: People, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
        { label: 'Employees', path: '/employees', icon: Business, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
      ]
    }
  ];

  const approvalItems = [
    { label: 'Timesheet Approvals', path: '/timesheets/approvals', icon: CheckCircle, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
    { label: 'Leave Approvals', path: '/leaves/approvals', icon: Approval, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
    { label: 'Leave Calendar', path: '/leaves/calendar', icon: CalendarMonth, roles: [UserRole.EMPLOYEE, UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
    { label: 'Project Assignments', path: '/project-assignments', icon: Assignment, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] },
  ];

  const estimationItems = [
    { label: 'Advanced Estimation', path: '/estimation/advanced', icon: Analytics, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ROOT_USER] },
    { label: 'Estimation Analytics', path: '/estimation/analytics', icon: Assessment, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ROOT_USER] },
    { label: 'Estimation Templates', path: '/estimation/templates', icon: Settings, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ROOT_USER] },
    { label: 'Estimation Config', path: '/estimation/config', icon: AdminPanelSettings, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ROOT_USER] },
  ];

  const renderNavigationItem = (item: any, isMobile: boolean = false) => {
    if (!isVisible(item.roles)) return null;

    const Icon = item.icon;
    const active = isActive(item.path);

    if (isMobile) {
      return (
        <ListItem
          key={item.path}
          onClick={() => handleNavigation(item.path)}
          sx={{
            borderRadius: 1,
            mb: 0.5,
            backgroundColor: active ? 'primary.main' : 'transparent',
            color: active ? 'white' : 'inherit',
            '&:hover': {
              backgroundColor: active ? 'primary.dark' : 'action.hover',
            },
          }}
        >
          <ListItemIcon sx={{ color: active ? 'white' : 'inherit', minWidth: 40 }}>
            <Icon />
          </ListItemIcon>
          <ListItemText primary={item.label} />
          {active && <ChevronRight sx={{ color: 'white' }} />}
        </ListItem>
      );
    }

    return (
      <Button
        key={item.path}
        onClick={() => handleNavigation(item.path)}
        startIcon={<Icon />}
        sx={{
          color: active ? 'white' : 'rgba(255, 255, 255, 0.9)',
          backgroundColor: active ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
          borderRadius: 3,
          px: 2,
          py: 1,
          mx: 0.5,
          textTransform: 'none',
          fontWeight: active ? 700 : 600,
          fontSize: '0.95rem',
          minWidth: 'auto',
          fontFamily: '"Inter", "Roboto", sans-serif',
          letterSpacing: '-0.2px',
          '&:hover': {
            backgroundColor: active ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
            color: 'white',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: active ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
        }}
      >
        {item.label}
      </Button>
    );
  };

  const renderDropdownNav = (category: any) => {
    const Icon = category.icon;
    const isOpen = Boolean(dropdownAnchors[category.label]);
    const hasActiveItem = category.items.some((item: any) => item.path && isActive(item.path));

    return (
      <React.Fragment key={category.label}>
        <Button
          onClick={(e) => handleDropdownOpen(category.label, e)}
          startIcon={<Icon />}
          endIcon={<ExpandMore sx={{ transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />}
          sx={{
            color: hasActiveItem ? 'white' : 'rgba(255, 255, 255, 0.9)',
            backgroundColor: hasActiveItem ? 'rgba(255, 255, 255, 0.2)' : 'transparent',
            borderRadius: 3,
            px: 2,
            py: 1,
            mx: 0.5,
            textTransform: 'none',
            fontWeight: hasActiveItem ? 700 : 600,
            fontSize: '0.95rem',
            minWidth: 'auto',
            fontFamily: '"Inter", "Roboto", sans-serif',
            letterSpacing: '-0.2px',
            '&:hover': {
              backgroundColor: hasActiveItem ? 'rgba(255, 255, 255, 0.25)' : 'rgba(255, 255, 255, 0.15)',
              color: 'white',
              transform: 'translateY(-1px)',
            },
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            boxShadow: hasActiveItem ? '0 4px 12px rgba(0, 0, 0, 0.15)' : 'none',
          }}
        >
          {category.label}
        </Button>

        <Menu
          anchorEl={dropdownAnchors[category.label]}
          open={isOpen}
          onClose={() => handleDropdownClose(category.label)}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          PaperProps={{
            sx: {
              mt: 1.5,
              minWidth: 220,
              boxShadow: '0 8px 32px rgba(51, 65, 85, 0.15)',
              borderRadius: 3,
              border: '1px solid rgba(255, 255, 255, 0.1)',
              backdropFilter: 'blur(20px)',
              background: 'rgba(255, 255, 255, 0.95)',
            },
          }}
        >
          {category.items.map((item: any) => {
            const ItemIcon = item.icon;
            const active = item.path && isActive(item.path);
            
            return (
              <MenuItem
                key={item.path || item.action}
                onClick={() => handleDropdownItemClick(item)}
                sx={{
                  py: 1.5,
                  px: 2.5,
                  backgroundColor: active ? 'rgba(51, 65, 85, 0.1)' : 'transparent',
                  color: active ? '#334155' : '#2c3e50',
                  borderRadius: 2,
                  mx: 1,
                  my: 0.5,
                  fontFamily: '"Inter", "Roboto", sans-serif',
                  fontWeight: active ? 600 : 500,
                  fontSize: '0.9rem',
                  '&:hover': {
                    backgroundColor: active ? 'rgba(51, 65, 85, 0.15)' : 'rgba(51, 65, 85, 0.05)',
                    color: '#334155',
                    transform: 'translateX(4px)',
                  },
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                }}
              >
                <ListItemIcon sx={{ color: active ? '#334155' : '#5a6c7d', minWidth: 36 }}>
                  <ItemIcon />
                </ListItemIcon>
                <ListItemText primary={item.label} />
              </MenuItem>
            );
          })}
        </Menu>
      </React.Fragment>
    );
  };

  const renderMobileMenu = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={() => setMobileMenuOpen(false)}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          backgroundColor: theme.palette.background.paper,
        },
      }}
    >
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" sx={{ fontWeight: 600, color: 'primary.main' }}>
            Tyvra PM
          </Typography>
          <IconButton onClick={() => setMobileMenuOpen(false)}>
            <Close />
          </IconButton>
        </Box>
      </Box>

      <List sx={{ px: 1, py: 2 }}>
        {navigationItems.map((category, categoryIndex) => (
          <React.Fragment key={category.category}>
            <ListItem sx={{ py: 1 }}>
              <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
                {category.category}
              </Typography>
            </ListItem>
            {category.items.map(item => renderNavigationItem(item, true))}
            {categoryIndex < navigationItems.length - 1 && <Divider sx={{ my: 1 }} />}
          </React.Fragment>
        ))}
        
        <Divider sx={{ my: 1 }} />
        
        <ListItem sx={{ py: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
            Approvals
          </Typography>
        </ListItem>
        {approvalItems.map(item => renderNavigationItem(item, true))}
        
        <Divider sx={{ my: 1 }} />
        
        <ListItem sx={{ py: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
            Analytics
          </Typography>
        </ListItem>
        {isVisible([UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER]) && (
          <>
            {renderNavigationItem({ label: 'Productivity Analytics', path: '/productivity-analytics', icon: Analytics, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] }, true)}
            {renderNavigationItem({ label: 'Historical Analytics', path: '/estimation/analytics', icon: Analytics, roles: [UserRole.MANAGER, UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] }, true)}
            {renderNavigationItem({ label: 'Leadership Dashboard', path: '/leadership-dashboard', icon: Assessment, roles: [UserRole.SUPER_MANAGER, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ROOT_USER] }, true)}
          </>
        )}
        
        <Divider sx={{ my: 1 }} />
        
        <ListItem sx={{ py: 1 }}>
          <Typography variant="overline" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '0.75rem' }}>
            Estimation Tools
          </Typography>
        </ListItem>
        {estimationItems.map(item => renderNavigationItem(item, true))}
        
        {/* Create Project Button for Mobile */}
        {isVisible([UserRole.ADMIN, UserRole.ROOT_USER, UserRole.HR_ADMIN, UserRole.SUPER_MANAGER]) && (
          <>
            <Divider sx={{ my: 1 }} />
            <ListItem
              onClick={handleCreateProject}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                backgroundColor: 'primary.main',
                color: 'white',
                '&:hover': {
                  backgroundColor: 'primary.dark',
                },
              }}
            >
              <ListItemIcon sx={{ color: 'white', minWidth: 40 }}>
                <Add />
              </ListItemIcon>
              <ListItemText primary="Create Project" />
            </ListItem>
          </>
        )}
      </List>
    </Drawer>
  );

  return (
    <>
      <AppBar 
        position="fixed" 
        elevation={3}
        sx={{ 
          background: 'linear-gradient(135deg, #334155 0%, #475569 50%, #64748b 100%)',
          boxShadow: '0 4px 20px rgba(51, 65, 85, 0.2)',
          zIndex: theme.zIndex.drawer + 1,
          backdropFilter: 'blur(10px)',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: { xs: 2, sm: 3 } }}>
          {/* Logo and Brand */}
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 800,
                color: 'white',
                fontSize: { xs: '1.3rem', sm: '1.5rem' },
                cursor: 'pointer',
                letterSpacing: '-0.8px',
                textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
                fontFamily: '"Inter", "Roboto", sans-serif',
              }}
              onClick={() => handleNavigation('/dashboard')}
            >
              Tyvra PM
            </Typography>
          </Box>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, gap: 0.5 }}>
              {/* Main navigation items */}
              {mainNavItems
                .filter(item => isVisible(item.roles))
                .map(item => renderNavigationItem(item))
              }
              
              {/* Categorized navigation with dropdowns */}
              {categorizedNav
                .filter(category => isVisible(category.roles))
                .map(category => renderDropdownNav(category))
              }
            </Box>
          )}

          {/* Mobile Menu Button */}
          {isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton
                color="inherit"
                aria-label="open drawer"
                edge="start"
                onClick={() => setMobileMenuOpen(true)}
              >
                <MenuIcon />
              </IconButton>
            </Box>
          )}

          {/* Right Side Actions */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: 'auto' }}>
            {/* Notifications */}
            <Tooltip title="Notifications">
              <IconButton color="inherit" sx={{ position: 'relative' }}>
                <Badge badgeContent={0} color="error">
                  <Notifications />
                </Badge>
              </IconButton>
            </Tooltip>

            {/* User Profile */}
            <Tooltip title={user?.firstName ? `${user.firstName} ${user.lastName}` : 'User Profile'}>
              <IconButton
                onClick={handleProfileMenuOpen}
                sx={{ ml: 1 }}
              >
                <Avatar
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontSize: '0.875rem',
                    fontWeight: 600,
                  }}
                >
                  {user?.firstName ? `${user.firstName[0]}${user.lastName[0]}` : 'U'}
                </Avatar>
              </IconButton>
            </Tooltip>

            {/* Profile Menu */}
            <Menu
              anchorEl={profileMenuAnchor}
              open={Boolean(profileMenuAnchor)}
              onClose={handleProfileMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              PaperProps={{
                sx: {
                  mt: 1,
                  minWidth: 200,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                },
              }}
            >
              <Box sx={{ px: 2, py: 1, borderBottom: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  {user?.firstName ? `${user.firstName} ${user.lastName}` : 'User'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user?.email || 'user@example.com'}
                </Typography>
                <Chip 
                  label={user?.role?.replace('_', ' ') || 'User'} 
                  size="small" 
                  color="primary" 
                  sx={{ mt: 1, fontSize: '0.75rem' }}
                />
              </Box>
              <MenuItem onClick={() => { handleNavigation('/profile'); handleProfileMenuClose(); }}>
                <ListItemIcon>
                  <Avatar sx={{ width: 24, height: 24 }} />
                </ListItemIcon>
                Profile
              </MenuItem>
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Close />
                </ListItemIcon>
                Logout
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Mobile Menu Drawer */}
      {renderMobileMenu()}

      {/* Create Project Modal */}
      <CreateProjectModal
        open={createProjectModalOpen}
        onClose={() => setCreateProjectModalOpen(false)}
        onSuccess={handleCreateProjectSuccess}
        onError={handleCreateProjectError}
      />

      {/* Project Creation Status Alert */}
      {projectCreateStatus && (
        <Alert 
          severity={projectCreateStatus.includes('Error') ? 'error' : 'success'}
          sx={{ 
            position: 'fixed', 
            top: 80, 
            right: 20, 
            zIndex: 9999,
            minWidth: 300,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {projectCreateStatus}
        </Alert>
      )}

      {/* Spacer for fixed AppBar */}
      <Toolbar />
    </>
  );
};

export default Navbar;